import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { capitalize, mapValues } from 'lodash';
import { BsModalRef, BsModalService } from 'ngx-bootstrap';

import { AICM_GPT_MODELS, AICMOpenAIModels, DefaultModel, Reaction } from 'ideta-library/lib/common/aicm';

import { AutoReplyService } from '../../services/auto-reply/auto-reply.service';
import { FeedbackService } from '../../services/feedback/feedback.service';
import { ToasterService } from 'app/shared/services/toaster/toaster.service';

import { ModalPromptGeneratorComponent } from './modal-prompt-generator/modal-prompt-generator.component';

export type PromptTemplate = 'default' | 'real_estate';

@Component({
  selector: 'app-chatgpt-options',
  templateUrl: './chatgpt-options.component.html',
  styleUrls: ['./chatgpt-options.component.scss']
})
export class ChatGPTOptionsComponent implements OnInit {
  @Input() api_key: string;
  @Input() prompt_text_private: string;
  @Input() prompt_text_public: string;
  @Input() isValidApiKey?: boolean;
  @Input() testedApiKey?: boolean;
  @Input() displayAllowedReactions: Reaction[];
  @Input() reactions: Reaction[];
  @Input() mode: string;
  @Input() channel: string;
  @Input() model?: AICMOpenAIModels;
  @Input() useIdetaKey?: boolean;

  @Output() doAddReaction: EventEmitter<any>;
  @Output() doRemoveReaction: EventEmitter<any>;
  @Output() api_key_update: EventEmitter<string>;
  @Output() prompt_text_private_update: EventEmitter<string>;
  @Output() prompt_text_public_update: EventEmitter<string>;
  @Output() modelOutput: EventEmitter<AICMOpenAIModels>;
  @Output() idetaKeyUsed: EventEmitter<boolean>;

  spinning: boolean;
  reaction: Reaction;
  showOptions: boolean;
  modelForm: FormControl;
  promptTemplatePrivateForm: FormControl;
  promptTemplatePublicForm: FormControl;
  dicoModelLength: number;
  dicoModel: { [key in AICMOpenAIModels]?: string };
  dicoTemplatePrivate: { [key in PromptTemplate]: string };
  dicoTemplatePublic: { [key in PromptTemplate]: string };
  addedPublic: boolean;
  addedPrivate: boolean;
  selectedModelInfo: any;

  currentPrivateTemplate: string;
  currentPublicTemplate: string;
  templatePrivatePrompt: string;
  templatePublicPrompt: string;
  validModel: boolean;
  bsModalRef: BsModalRef;

  constructor(
    private autoReplyService: AutoReplyService,
    private toasterService: ToasterService,
    private modalService: BsModalService,
    private feedbackService: FeedbackService
  ) {
    this.api_key_update = new EventEmitter();
    this.prompt_text_private_update = new EventEmitter();
    this.prompt_text_public_update = new EventEmitter();
    this.doAddReaction = new EventEmitter();
    this.doRemoveReaction = new EventEmitter();
    this.modelOutput = new EventEmitter();
    this.idetaKeyUsed = new EventEmitter();
  }

  ngOnInit() {
    this.spinning = false;
    this.validModel = true;
    this.modelForm = new FormControl();
    this.promptTemplatePrivateForm = new FormControl();
    this.promptTemplatePublicForm = new FormControl();
    this.promptTemplatePrivateForm.setValue('default');
    this.promptTemplatePublicForm.setValue('default');
    this.modelForm.setValue(this.model || DefaultModel);
    this.selectedModelInfo = AICM_GPT_MODELS[this.modelForm.value];
    this.buildDicoModel();
    this.buildPromptTemplate();
    this.addedPrivate = this.prompt_text_private != '';
    this.addedPublic = this.prompt_text_public != '';
    this.showOptions = false;
  }

  private buildDicoModel(): void {
    this.dicoModel = mapValues(AICM_GPT_MODELS, 'name');
    this.dicoModelLength = Object.keys(this.dicoModel).length;
  }

  private buildPromptTemplate(): void {
    this.dicoTemplatePrivate = {
      default: 'Default',
      real_estate: 'Real Estate'
    };
    this.dicoTemplatePublic = {
      default: 'Default',
      real_estate: 'Real Estate'
    };
  }

  changePrompt(setting: string) {
    if (setting == 'private') {
      if (this.prompt_text_private != this.templatePrivatePrompt) {
        this.feedbackService
          .showMessage({
            message: 'idechat.chatgpt-change-template-message',
            title: 'idechat.chatgpt-change-template-title',
            type: 'confirm',
            decoration: 'warning',
            messageParams: {
              length: 1
            }
          })
          .then((isConfirmed: boolean) => {
            if (isConfirmed) {
              this.setPromptTemplate(setting);
            } else {
              this.promptTemplatePrivateForm.setValue(this.currentPrivateTemplate);
            }
          });
      } else this.setPromptTemplate(setting);
    } else {
      if (this.prompt_text_public != this.templatePublicPrompt) {
        this.feedbackService
          .showMessage({
            message: 'idechat.chatgpt-change-template-message',
            title: 'idechat.chatgpt-change-template-title',
            type: 'confirm',
            decoration: 'warning',
            messageParams: {
              length: 1
            }
          })
          .then((isConfirmed: boolean) => {
            if (isConfirmed) {
              this.setPromptTemplate(setting);
            } else {
              this.promptTemplatePublicForm.setValue(this.currentPublicTemplate);
            }
          });
      } else this.setPromptTemplate(setting);
    }
  }

  setPromptTemplate(setting: string) {
    const capitalizedChannel: string = capitalize(this.channel);
    const account: string = this.channel === 'instagram' ? 'account' : 'page';
    if (setting === 'private') {
      this.currentPrivateTemplate = this.promptTemplatePrivateForm.value;
      switch (this.promptTemplatePrivateForm.value) {
        case 'default':
          this.templatePrivatePrompt =
            'Act as a community manager on ' +
            capitalizedChannel +
            '.\nThe ' +
            account +
            ' you work for does a product named ABC with the 3 main features :\n- feature 1\n- feature 2\n- feature 3\n an user commented on a post saying {{post}} the following comment :{{comment}}.\nThe commenter name is {{fullName}}. \nWrite an engaging message between 20 and 30 words to be sent privately to their inbox and make sure to include the link to our website : https://ideta.io';
          this.setPrompt('private', this.templatePrivatePrompt);
          break;
        case 'real_estate':
          this.templatePrivatePrompt =
            'Act as a community manager for Yuna Real Estate on ' +
            capitalizedChannel +
            '.\n\nYuna Real Estate is an agency specializing in selling houses and apartments.\n\nAn user commented on a post saying {{post}} the following comment : {{comment}}\n\n' +
            "The commenter's name is {{fullName}}.\n\nIf the user expresses interest in the property, provide them with our contact information and ask about their property search preferences.\n\n" +
            "If you don't know the answer to the commenter's question, tell them to call us.\n\n" +
            'email :  sarah@yuna-realestate.com\n' +
            'tel : 00 00 00 00 00\n\n' +
            'Ensure the message is engaging.\n\nThe message should be between 20 and 40 words.\n\nInclude the link to our website: https://yuna-realestate.com\n' +
            "\n\nIt's a private message.";
          this.setPrompt('private', this.templatePrivatePrompt);
          break;
      }
    } else {
      this.currentPublicTemplate = this.promptTemplatePublicForm.value;
      switch (this.promptTemplatePublicForm.value) {
        case 'default':
          this.templatePublicPrompt =
            'The ' +
            account +
            ' you work for does a product named ABC with the 3 main features :\n- feature 1 \n- feature 2\n- feature 3\n an user commented on a post saying {{post}} the following comment :{{comment}}.\n Write an engaging public comment visible to the community to answer it.\nBe nice/energetic/ and, if possible, try to ask a question so that they engage more on the post and with the page.\nThe commenter name is {{fullName}}.\nMake it 10 to 30 words long.';
          this.setPrompt('public', this.templatePublicPrompt);
          break;
        case 'real_estate':
          this.templatePublicPrompt =
            'Act as a community manager for Yuna Real Estate on ' +
            capitalizedChannel +
            '.\n\nYuna Real Estate is an agency specializing in selling houses and apartments.\n\nAn user commented on a post saying {{post}} the following comment : {{comment}}\n\n' +
            "The commenter's name is {{fullName}}.\n\n If the user expresses interest in the property, tell him that we will contact him in private message.\n\n" +
            "If you don't know the answer to the commenter's question, tell them to send them a private message.\n\n" +
            'Write an engaging public comment visible to the community to answer it.\n' +
            'Be nice/energetic/ and, if possible, try to ask a question so that they engage more on the post and with the page.\n\n' +
            'Make it 10 to 30 words long.\n\nOur website: https://yuna-realestate.com';
          this.setPrompt('public', this.templatePublicPrompt);
          break;
      }
    }
  }

  setModel() {
    this.validModel = true;
    this.modelOutput.emit(this.modelForm.value);
    this.selectedModelInfo = AICM_GPT_MODELS[this.modelForm.value];
  }

  setAPIKey(key: string) {
    this.isValidApiKey = false;
    this.api_key = key;
    this.api_key_update.emit('');
  }

  setReaction(reaction: Reaction) {
    this.reaction = reaction;
  }

  setPrompt(setting: string, prompt: string) {
    if (setting === 'private') {
      this.prompt_text_private = prompt;
      this.prompt_text_private_update.emit(prompt);
    } else {
      this.prompt_text_public = prompt;
      this.prompt_text_public_update.emit(prompt);
    }
  }

  setDefaultPrompt(setting: string) {
    if (setting === 'private') {
      this.addedPrivate = !this.addedPrivate;
      if (this.addedPrivate) {
        this.currentPrivateTemplate = 'default';
        this.promptTemplatePrivateForm.setValue('default');
        this.setPromptTemplate(setting);
      } else this.setPrompt('private', '');
    }
    if (setting === 'public') {
      this.addedPublic = !this.addedPublic;
      if (this.addedPublic) {
        this.currentPublicTemplate = 'default';
        this.promptTemplatePublicForm.setValue('default');
        this.setPromptTemplate(setting);
      } else this.setPrompt('public', '');
    }
  }

  async testAPIKey() {
    this.spinning = true;
    this.testedApiKey = true;
    let isValid = await this.autoReplyService.validateApiKey({ model: this.model, apiKey: this.api_key });

    if (isValid['validity']) {
      this.spinning = false;
      this.isValidApiKey = true;
      this.api_key_update.emit(this.api_key);
    } else {
      this.spinning = false;
      this.isValidApiKey = false;
    }
  }

  addReaction() {
    this.doAddReaction.emit(this.reaction);
    this.reaction = null;
  }

  removeReaction(i: number) {
    this.doRemoveReaction.emit(i);
  }

  addText(whereToAdd: string, e: any, text) {
    let addition = '';
    switch (text) {
      case 'name':
        addition = '{{fullName}}';
        break;
      case 'comment':
        addition = '{{comment}}';
        break;
      case 'post':
        addition = '{{post}}';
        break;
      case 'postId':
        addition = '{{postId}}';
        break;
      case 'campaignId':
        addition = '{{campaignId}}';
        break;
    }
    const caretPosition = e.selectionStart;
    // M
    let split_array;
    if (whereToAdd === 'private') {
      split_array = this.prompt_text_private.split('');
      split_array.splice(caretPosition, 0, addition);
      this.prompt_text_private = split_array.join('');
    } else if (whereToAdd === 'public') {
      split_array = this.prompt_text_public.split('');
      split_array.splice(caretPosition, 0, addition);
      this.prompt_text_public = split_array.join('');
    }
  }

  async toggleUseOwnKey(toggle) {
    if (!toggle) {
      this.toasterService.showToast('success', {
        title: 'Success',
        message: 'You are now using Idetas OpenAI API Key!'
      });
    }
    this.useIdetaKey = !toggle;
    this.isValidApiKey = !toggle;
    this.testedApiKey = !toggle;
    this.idetaKeyUsed.emit(!toggle);
    this.setModel();
    this.setAPIKey('');
    this.api_key_update.emit(this.api_key);
  }

  showPromptGeneratorModal(replyType: string) {
    const initialState = {
      replyType: replyType,
      channel: capitalize(this.channel)
    };
    this.bsModalRef = this.modalService.show(ModalPromptGeneratorComponent, {
      class: `modal-dialog-centered modal-md`,
      initialState
    });
    this.bsModalRef.content.event.subscribe(res => {
      if (res) {
        if (res.replyType === 'private') {
          this.setPrompt('private', res.data);
        } else this.setPrompt('public', res.data);
      }
    });
  }

  detectPromptChange() {}
}
